import React, { useEffect, useState } from 'react';
import { api } from '../utils/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { X, Plus, Trash2, Loader2, Save } from 'lucide-react';

const InvoiceCreateModal = ({ onClose, onCreated, editInvoice = null }) => {
  const [customers, setCustomers] = useState([]);
  const [profile, setProfile] = useState(null);
  const [billingProfiles, setBillingProfiles] = useState([]);
  const [billingProfileId, setBillingProfileId] = useState('');
  const [items, setItems] = useState(
    editInvoice?.items?.length
      ? editInvoice.items.map(i => ({
          description: i.description, quantity: i.quantity, unit: i.unit,
          unit_price: i.unit_price, vat_rate: i.vat_rate,
        }))
      : [{ description: '', quantity: 1, unit: 'st', unit_price: 0, vat_rate: 25 }]
  );
  const [customerId, setCustomerId] = useState(editInvoice?.customer_id || '');
  const [reference, setReference] = useState(editInvoice?.reference || '');
  const [notes, setNotes] = useState(editInvoice?.notes || '');
  const [issueDate, setIssueDate] = useState(
    editInvoice?.issue_date || new Date().toISOString().slice(0, 10)
  );
  const [issueDateManuallySet, setIssueDateManuallySet] = useState(
    !!editInvoice?.issue_date_manually_set
  );
  const [generatingOcr, setGeneratingOcr] = useState(false);
  const [dueDays, setDueDays] = useState(
    editInvoice
      ? parseInt((editInvoice.payment_terms || '30').match(/\d+/)?.[0] || '30')
      : 30
  );
  const [saving, setSaving] = useState(false);
  const isEdit = !!editInvoice;

  useEffect(() => {
    (async () => {
      try {
        const [cRes, pRes, bpRes] = await Promise.all([
          api.listInvoiceCustomers(),
          api.getBillingProfile(),
          api.listBillingProfiles().catch(() => ({ data: [] })),
        ]);
        setCustomers(cRes.data);
        setProfile(pRes.data);
        setBillingProfiles(bpRes.data || []);
        if (!isEdit) {
          setDueDays(pRes.data?.default_payment_terms_days || 30);
          const def = (bpRes.data || []).find(b => b.is_default);
          if (def) setBillingProfileId(def.id);
        } else {
          setBillingProfileId(editInvoice.billing_profile_id || '');
        }
        if (!isEdit && cRes.data?.length === 1) setCustomerId(cRes.data[0].id);
      } catch { /* ignore */ }
    })();
  }, [isEdit, editInvoice]);

  const setItem = (idx, k, v) => setItems(arr => arr.map((it, i) => i === idx ? { ...it, [k]: v } : it));
  const addItem = () => setItems(arr => [...arr, { description: '', quantity: 1, unit: 'st', unit_price: 0, vat_rate: profile?.default_vat_rate || 25 }]);
  const delItem = (idx) => setItems(arr => arr.length > 1 ? arr.filter((_, i) => i !== idx) : arr);

  const totals = items.reduce((acc, it) => {
    const sub = (parseFloat(it.quantity) || 0) * (parseFloat(it.unit_price) || 0);
    const vat = sub * ((parseFloat(it.vat_rate) || 0) / 100);
    acc.subtotal += sub; acc.vat += vat;
    return acc;
  }, { subtotal: 0, vat: 0 });
  const total = totals.subtotal + totals.vat;

  const handleGenerateOcr = async () => {
    setGeneratingOcr(true);
    try {
      const res = await api.generateOcr();
      setReference(res.data.ocr);
      toast.success(`OCR ${res.data.ocr} genererat`);
    } catch (err) {
      toast.error('Kunde inte generera OCR');
    } finally { setGeneratingOcr(false); }
  };

  const handleCreate = async () => {
    if (!customerId) { toast.error('Välj kund'); return; }
    if (items.length === 0 || !items[0].description) { toast.error('Lägg till minst en rad med beskrivning'); return; }
    if (reference && reference.trim().length < 3) {
      toast.error('Referens måste vara minst 3 tecken (eller lämna tomt)');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        customer_id: customerId,
        items: items.map(it => ({
          description: it.description,
          quantity: parseFloat(it.quantity) || 1,
          unit: it.unit,
          unit_price: parseFloat(it.unit_price) || 0,
          vat_rate: parseFloat(it.vat_rate) || 25,
        })),
        due_days: parseInt(dueDays) || 30,
        notes: notes || null,
        reference: reference ? reference.trim() : null,
      };
      // Skicka bara issue_date om admin ändrat det manuellt (annars låt backend bestämma default)
      if (issueDateManuallySet && issueDate) payload.issue_date = issueDate;
      if (billingProfileId) payload.billing_profile_id = billingProfileId;
      let res;
      if (isEdit) {
        res = await api.updateInvoice(editInvoice.id, payload);
        toast.success(`Faktura ${res.data.invoice_number} uppdaterad`);
      } else {
        res = await api.createInvoice(payload);
        toast.success(`Faktura ${res.data.invoice_number} skapad som utkast`);
      }
      onCreated?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || (isEdit ? 'Kunde inte uppdatera faktura' : 'Kunde inte skapa faktura'));
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center px-3 py-6" onClick={onClose} data-testid="invoice-create-modal">
      <div className="bg-card rounded-2xl shadow-2xl border border-border max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="bg-[#1A1A1A] text-white px-6 py-4 flex items-center justify-between">
          <h2 className="text-base font-bold">{isEdit ? `Redigera utkast — ${editInvoice.invoice_number}` : 'Ny faktura'}</h2>
          <button onClick={onClose} className="hover:bg-white/10 rounded p-1"><X size={16} /></button>
        </div>
        <div className="p-6 overflow-y-auto space-y-5">
          {billingProfiles.length > 1 && (
            <div>
              <Label className="text-xs">Fakturera från (bolag) *</Label>
              <select value={billingProfileId} onChange={(e) => setBillingProfileId(e.target.value)}
                className="mt-1 w-full h-10 px-3 rounded-md border border-border bg-background text-sm"
                data-testid="ic-billing-profile-select">
                {billingProfiles.map(b => (
                  <option key={b.id} value={b.id}>
                    {b.label || b.company_name}{b.is_default ? ' (standard)' : ''}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Kund *</Label>
              <select value={customerId} onChange={(e) => setCustomerId(e.target.value)}
                className="mt-1 w-full h-10 px-3 rounded-md border border-border bg-background text-sm"
                data-testid="ic-customer-select">
                <option value="">Välj kund...</option>
                {customers.map(c => <option key={c.id} value={c.id}>{c.company_name || c.contact_person}</option>)}
              </select>
              {customers.length === 0 && <p className="text-[11px] text-amber-700 mt-1">Inga kunder. Skapa kunder först via "Kunder"-knappen.</p>}
            </div>
            <div>
              <Label className="text-xs">Fakturadatum</Label>
              <Input
                type="date"
                value={issueDate}
                onChange={(e) => { setIssueDate(e.target.value); setIssueDateManuallySet(true); }}
                className="mt-1"
                data-testid="ic-issue-date"
              />
              <p className="text-[10px] text-muted-foreground mt-0.5">
                Uppdateras automatiskt till projektets slutdatum när tidrapporter kopplas
              </p>
            </div>
            <div>
              <Label className="text-xs">Förfallodagar</Label>
              <Input type="number" value={dueDays} onChange={(e) => setDueDays(e.target.value)} className="mt-1" data-testid="ic-due-days" />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-xs">Er referens / OCR</Label>
              <div className="flex gap-2 mt-1">
                <Input
                  value={reference}
                  onChange={(e) => setReference(e.target.value)}
                  className="flex-1"
                  placeholder="Projektnamn, inköpsorder eller OCR-nummer (min 3 tecken)"
                  data-testid="ic-reference"
                  minLength={3}
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleGenerateOcr}
                  disabled={generatingOcr}
                  className="whitespace-nowrap"
                  data-testid="ic-gen-ocr-btn"
                >
                  {generatingOcr ? <Loader2 size={14} className="animate-spin" /> : 'Generera OCR'}
                </Button>
              </div>
              <p className="text-[10px] text-muted-foreground mt-1">
                Lämna tomt så visas fakturans OCR-nummer automatiskt. Egen referens måste vara minst 3 tecken.
              </p>
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-xs">Fakturarader</Label>
              <Button size="sm" variant="outline" onClick={addItem} className="h-8 text-xs" data-testid="ic-add-row">
                <Plus size={12} className="mr-1" /> Lägg till rad
              </Button>
            </div>
            <div className="space-y-2">
              {items.map((it, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-end" data-testid={`ic-row-${i}`}>
                  <div className="col-span-12 sm:col-span-5">
                    <Input value={it.description} onChange={(e) => setItem(i, 'description', e.target.value)} placeholder="Beskrivning" className="h-9" data-testid={`ic-desc-${i}`} />
                  </div>
                  <div className="col-span-3 sm:col-span-1">
                    <Input type="number" value={it.quantity} onChange={(e) => setItem(i, 'quantity', e.target.value)} placeholder="Antal" className="h-9" />
                  </div>
                  <div className="col-span-3 sm:col-span-1">
                    <Input value={it.unit} onChange={(e) => setItem(i, 'unit', e.target.value)} placeholder="st" className="h-9" />
                  </div>
                  <div className="col-span-3 sm:col-span-2">
                    <Input type="number" value={it.unit_price} onChange={(e) => setItem(i, 'unit_price', e.target.value)} placeholder="À-pris" className="h-9" data-testid={`ic-price-${i}`} />
                  </div>
                  <div className="col-span-2 sm:col-span-2">
                    <Input type="number" value={it.vat_rate} onChange={(e) => setItem(i, 'vat_rate', e.target.value)} placeholder="Moms %" className="h-9" />
                  </div>
                  <div className="col-span-1 flex justify-end">
                    {items.length > 1 && (
                      <button onClick={() => delItem(i)} className="text-red-600 hover:text-red-700 p-2" data-testid={`ic-del-${i}`}>
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div>
            <Label className="text-xs">Meddelande / Anteckningar</Label>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm" data-testid="ic-notes" />
          </div>

          <div className="bg-muted/40 rounded-xl p-4 border border-border">
            <div className="flex justify-between text-sm"><span>Delsumma</span><span>{totals.subtotal.toLocaleString('sv-SE', { minimumFractionDigits: 2 })} kr</span></div>
            <div className="flex justify-between text-sm"><span>Moms</span><span>{totals.vat.toLocaleString('sv-SE', { minimumFractionDigits: 2 })} kr</span></div>
            <div className="flex justify-between text-lg font-bold mt-2 pt-2 border-t border-border"><span>Att betala</span><span className="text-[#E8613A]" data-testid="ic-total">{total.toLocaleString('sv-SE', { minimumFractionDigits: 2 })} kr</span></div>
          </div>
        </div>
        <div className="px-6 py-4 border-t border-border bg-muted/30 flex items-center justify-between">
          <button onClick={onClose} className="text-sm text-muted-foreground hover:text-foreground">Avbryt</button>
          <Button onClick={handleCreate} disabled={saving} className="bg-[#E8613A] hover:bg-[#D4532F] text-white rounded-full h-10 px-6" data-testid="ic-create-btn">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <><Save size={14} className="mr-1.5" /> {isEdit ? 'Spara ändringar' : 'Skapa utkast'}</>}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default InvoiceCreateModal;
